import { Component, OnInit, Output, EventEmitter, Inject, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { ExpensesService, Expense } from '../../features/expenses/services/expenses.service';
import { ChartOfAccountsService } from '../../features/chart-of-accounts/services/chart-of-accounts.service';
import { EmployeeService } from '../../features/employees/employee.service';
import { BankService } from '../../features/bank/services/bank.service';
import { CreditCardService } from '../../features/credit-card/services/credit-card.service';
import { UploadFileService } from '../../shared/services/file-upload.service';
import { Subject, ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CREATE_EXPENSE, UPDATE_EXPENSE } from '../../features/expenses/graphql/mutations/expenses.mutation';
import { Apollo } from 'apollo-angular';

// Custom date formats for DD/MM/YYYY display
export const DD_MM_YYYY_FORMAT = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-add-expense-dialog',
  templateUrl: './add-expense-dialog.html',
  styleUrls: ['./expenses.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatAutocompleteModule,
    MatCheckboxModule,
    ReactiveFormsModule,
  ],
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: DD_MM_YYYY_FORMAT },
  ]
})
export class AddExpenseDialogComponent implements OnInit, OnDestroy {
  @Output() expenseAdded = new EventEmitter<any>();
  expenseForm!: FormGroup;
  expenseData: any;
  loading: boolean = false;
  
  // File upload properties
  csvFile: File[] = [];
  url: string = '';
  
  // Searchable dropdown properties
  expenseTypeFilterCtrl: FormControl = new FormControl();
  filteredExpenseTypes: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);
  protected _onDestroy = new Subject<void>();
  
  // Account properties
  accounts: any[] = [];
  accountFilterCtrl: FormControl = new FormControl();
  filteredAccounts: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  
  // Employee properties
  employees: any[] = [];
  paidByFilterCtrl: FormControl = new FormControl();
  filteredPaidByEmployees: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  
  // Bank Account properties
  bankAccounts: any[] = [];
  bankAccountFilterCtrl: FormControl = new FormControl();
  filteredBankAccounts: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  
  // Credit Card properties
  creditCards: any[] = [];
  creditCardFilterCtrl: FormControl = new FormControl();
  filteredCreditCards: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  
  // Expense types array
  expenseTypes: string[] = [
    // Office & Administrative
    'Office Supplies', 'Office Equipment', 'Office Furniture', 'Stationery', 
    'Printing & Copying', 'Postage & Shipping',
    
    // Technology & Software
    'Software Licenses', 'Hardware & IT Equipment', 'Internet & Phone', 
    'Website & Domain', 'Cloud Services',
    
    // Travel & Transportation
    'Air Travel', 'Hotel Accommodation', 'Car Rental', 'Fuel & Parking', 
    'Public Transportation', 'Taxi & Ride Share', 'Mileage Reimbursement',
    
    // Meals & Entertainment
    'Business Meals', 'Client Entertainment', 'Coffee & Refreshments', 'Catering',
    
    // Professional Services
    'Legal Services', 'Accounting Services', 'Consulting Fees', 
    'Professional Memberships', 'Training & Education', 'Conferences & Seminars',
    
    // Marketing & Advertising
    'Advertising', 'Marketing Materials', 'Social Media Ads', 'Trade Shows', 'Promotional Items',
    
    // Facilities & Operations
    'Rent', 'Utilities', 'Maintenance & Repairs', 'Cleaning Services', 
    'Security Services', 'Insurance',
    
    // Employee Benefits
    'Health Insurance', 'Employee Benefits', 'Employee Recognition', 'Team Building',
    
    // Financial Services
    'Bank Fees', 'Credit Card Fees', 'Loan Interest', 'Foreign Exchange',
    
    // Miscellaneous
    'Subscriptions', 'Books & Publications', 'Gifts & Donations', 'Emergency Expenses', 'Other'
  ];
  
  // Date picker filter - allow past and future dates within reasonable range
  dateFilter = (date: Date | null): boolean => {
    if (!date) return false;
    
    const today = new Date();
    const oneYearAgo = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
    const oneYearFromNow = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());
    
    // Allow dates from 1 year ago to 1 year from now
    return date >= oneYearAgo && date <= oneYearFromNow;
  };
  
  // Mock data for dropdowns - these should be fetched from services
  branches = [
    { id: 1, name: 'Main Branch' },
    { id: 2, name: 'North Branch' },
    { id: 3, name: 'South Branch' }
  ];
  
  // Mock employee data - should be fetched from API
  mockEmployees = [
    { id: 1, name: 'John Doe' },
    { id: 2, name: 'Jane Smith' },
    { id: 3, name: 'Mike Johnson' }
  ];

  constructor(
    private fb: FormBuilder,
    private apollo: Apollo,
    private datePipe: DatePipe,
    private chartOfAccountsService: ChartOfAccountsService,
    private employeeService: EmployeeService,
    private bankService: BankService,
    private creditCardService: CreditCardService,
    private expensesService: ExpensesService,
    private toastr: ToastrService,
    private documentUploadService: UploadFileService,
    private cdr: ChangeDetectorRef,
    public dialogRef: MatDialogRef<AddExpenseDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
  }

  ngOnInit(): void {
    console.log('=== DIALOG COMPONENT INITIALIZATION ===');
    console.log('Dialog data received:', this.data);
    console.log('Dialog data type:', typeof this.data);
    
    // Set loading to true initially
    this.loading = true;
    
    if (this.data) {
      // Use fresh API data directly
      this.expenseData = this.data;
      console.log('=== USING FRESH API DATA ===');
      console.log('Raw expense data from API:', this.expenseData);
      console.log('Data structure keys:', Object.keys(this.expenseData));
      
      // Log all field values with their types
      console.log('=== FIELD VALUES ANALYSIS ===');
      console.log('- ID:', this.expenseData.id, '(type:', typeof this.expenseData.id, ')');
      console.log('- expenseDate:', this.expenseData.expenseDate, '(type:', typeof this.expenseData.expenseDate, ')');
      console.log('- expenseType:', this.expenseData.expenseType, '(type:', typeof this.expenseData.expenseType, ')');
      console.log('- paymentMethod:', this.expenseData.paymentMethod, '(type:', typeof this.expenseData.paymentMethod, ')');
      console.log('- accountId:', this.expenseData.accountId, '(type:', typeof this.expenseData.accountId, ')');
      console.log('- paidBy:', this.expenseData.paidBy, '(type:', typeof this.expenseData.paidBy, ')');
      console.log('- referenceNo:', this.expenseData.referenceNo, '(type:', typeof this.expenseData.referenceNo, ')');
      console.log('- description:', this.expenseData.description, '(type:', typeof this.expenseData.description, ')');
      console.log('- amount:', this.expenseData.amount, '(type:', typeof this.expenseData.amount, ')');
      console.log('- tax:', this.expenseData.tax, '(type:', typeof this.expenseData.tax, ')');
      console.log('- bankAccountId:', this.expenseData.bankAccountId, '(type:', typeof this.expenseData.bankAccountId, ')');
      console.log('- creditCardId:', this.expenseData.creditCardId, '(type:', typeof this.expenseData.creditCardId, ')');
      console.log('- isCheque:', this.expenseData.isCheque, '(type:', typeof this.expenseData.isCheque, ')');
      console.log('- chequeNo:', this.expenseData.chequeNo, '(type:', typeof this.expenseData.chequeNo, ')');
      console.log('- chequeDate:', this.expenseData.chequeDate, '(type:', typeof this.expenseData.chequeDate, ')');
      console.log('==============================');
    } else {
      console.log('No expense data received - this is ADD mode');
      this.expenseData = null;
    }
    
    this.initForm();
    this.initSearchableDropdown();
    this.fetchAccounts();
    this.fetchEmployees();
    this.fetchBankAccounts();
    this.fetchCreditCards();
    
    // Set dropdown values after data is loaded
    if (this.expenseData) {
      this.setDropdownValues();
    }
    
    // Set loading to false and force change detection after initialization
    setTimeout(() => {
      this.loading = false;
      this.cdr.detectChanges();
    }, 200);
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  setDropdownValues(): void {
    console.log('=== SETTING DROPDOWN VALUES ===');
    
    // Set expense type
    if (this.expenseData.expenseType) {
      console.log('Setting expense type:', this.expenseData.expenseType);
      this.expenseTypeFilterCtrl.setValue(this.expenseData.expenseType);
      this.expenseForm.patchValue({ expenseType: this.expenseData.expenseType });
    }

    // Set payment method (simple dropdown, not searchable) with case normalization
    if (this.expenseData.paymentMethod) {
      console.log('Setting payment method (original):', this.expenseData.paymentMethod);
      
      // Normalize payment method values to match form options
      let normalizedPaymentMethod = this.expenseData.paymentMethod;
      
      // Handle different API response formats
      if (this.expenseData.paymentMethod === 'BANK_TRANSFER' || this.expenseData.paymentMethod === 'BANK_ACCOUNT') {
        normalizedPaymentMethod = 'BANK';
      } else if (this.expenseData.paymentMethod === 'CREDIT_CARD') {
        normalizedPaymentMethod = 'CARD';
      } else if (this.expenseData.paymentMethod === 'CASH') {
        normalizedPaymentMethod = 'CASH';
      }
      // Handle case variations
      else if (this.expenseData.paymentMethod.toUpperCase() === 'BANK') {
        normalizedPaymentMethod = 'BANK';
      } else if (this.expenseData.paymentMethod.toUpperCase() === 'CARD') {
        normalizedPaymentMethod = 'CARD';
      } else if (this.expenseData.paymentMethod.toUpperCase() === 'CASH') {
        normalizedPaymentMethod = 'CASH';
      }
      
      console.log('Setting payment method (normalized):', normalizedPaymentMethod);
      this.expenseForm.patchValue({ paymentMethod: normalizedPaymentMethod });
      
      // Trigger change detection to show/hide conditional fields
      this.expenseForm.get('paymentMethod')?.markAsDirty();
      this.expenseForm.get('paymentMethod')?.updateValueAndValidity();
    }

    // Set reference number
    if (this.expenseData.referenceNo) {
      console.log('Setting reference number:', this.expenseData.referenceNo);
      this.expenseForm.patchValue({ referenceNo: this.expenseData.referenceNo });
    }

    // Set description
    if (this.expenseData.description) {
      console.log('Setting description:', this.expenseData.description);
      this.expenseForm.patchValue({ description: this.expenseData.description });
    }

    // Set approved by (if different from paidBy)
    if (this.expenseData.approvedBy) {
      console.log('Setting approved by:', this.expenseData.approvedBy);
      this.expenseForm.patchValue({ approvedBy: this.expenseData.approvedBy });
    }

    console.log('Form values after dropdown updates:', this.expenseForm.value);
    console.log('================================');

    // Set account, paid by, bank account, credit card - these need to wait for API data
    setTimeout(() => {
      this.setSearchableDropdownValues();
    }, 1000);
  }

  setSearchableDropdownValues(): void {
    console.log('=== SETTING SEARCHABLE DROPDOWN VALUES ===');
    console.log('Accounts loaded:', this.accounts.length);
    console.log('Employees loaded:', this.employees.length);
    console.log('Bank accounts loaded:', this.bankAccounts.length);
    console.log('Credit cards loaded:', this.creditCards.length);
    
    // Set account dropdown value after accounts are loaded
    if (this.expenseData.accountId && this.accounts.length > 0) {
      console.log('Looking for account with ID:', this.expenseData.accountId);
      const selectedAccount = this.accounts.find(acc => acc.id === this.expenseData.accountId);
      console.log('Found account:', selectedAccount);
      if (selectedAccount) {
        const displayValue = `${selectedAccount.accountName} (${selectedAccount.accountCode})`;
        console.log('Setting account display value:', displayValue);
        this.accountFilterCtrl.setValue(displayValue);
        this.expenseForm.patchValue({ accountId: selectedAccount.id });
      }
    }

    // Set paid by dropdown value after employees are loaded
    if (this.expenseData.paidBy && this.employees.length > 0) {
      console.log('Looking for employee with ID:', this.expenseData.paidBy);
      const selectedEmployee = this.employees.find(emp => emp.id === this.expenseData.paidBy);
      console.log('Found employee:', selectedEmployee);
      if (selectedEmployee) {
        const displayValue = `${selectedEmployee.firstName} ${selectedEmployee.lastName} (${selectedEmployee.employeeCode})`;
        console.log('Setting employee display value:', displayValue);
        this.paidByFilterCtrl.setValue(displayValue);
        this.expenseForm.patchValue({ paidBy: selectedEmployee.id });
      }
    }

    // Set bank account if applicable
    if (this.expenseData.bankAccountId && this.bankAccounts.length > 0) {
      console.log('Looking for bank account with ID:', this.expenseData.bankAccountId);
      const selectedBankAccount = this.bankAccounts.find(bank => bank.id === this.expenseData.bankAccountId);
      console.log('Found bank account:', selectedBankAccount);
      if (selectedBankAccount) {
        console.log('Setting bank account display value:', selectedBankAccount.bankName);
        this.bankAccountFilterCtrl.setValue(selectedBankAccount.bankName);
        this.expenseForm.patchValue({ bankAccountId: selectedBankAccount.id });
      }
    }

    // Set credit card if applicable
    if (this.expenseData.creditCardId && this.creditCards.length > 0) {
      console.log('Looking for credit card with ID:', this.expenseData.creditCardId);
      const selectedCreditCard = this.creditCards.find(card => card.id === this.expenseData.creditCardId);
      console.log('Found credit card:', selectedCreditCard);
      if (selectedCreditCard) {
        const displayValue = `${selectedCreditCard.cardName} (${selectedCreditCard.cardNumberMasked})`;
        console.log('Setting credit card display value:', displayValue);
        this.creditCardFilterCtrl.setValue(displayValue);
        this.expenseForm.patchValue({ creditCardId: selectedCreditCard.id });
      }
    }
    
    // Set isCheque checkbox and ensure conditional fields are visible
    console.log('=== SETTING CONDITIONAL FIELDS ===');
    console.log('isCheque value:', this.expenseData.isCheque);
    console.log('Payment method:', this.expenseForm.get('paymentMethod')?.value);
    
    if (this.expenseData.isCheque !== undefined && this.expenseData.isCheque !== null) {
      console.log('Setting isCheque checkbox to:', this.expenseData.isCheque);
      this.expenseForm.patchValue({ isCheque: this.expenseData.isCheque });
      this.expenseForm.get('isCheque')?.markAsDirty();
      this.expenseForm.get('isCheque')?.updateValueAndValidity();
    }
    
    // Force change detection to ensure conditional fields show/hide properly
    setTimeout(() => {
      console.log('Triggering change detection for conditional fields...');
      this.expenseForm.get('paymentMethod')?.markAsTouched();
      this.expenseForm.get('isCheque')?.markAsTouched();
      console.log('Current form state:');
      console.log('- Payment Method:', this.expenseForm.get('paymentMethod')?.value);
      console.log('- isCheque:', this.expenseForm.get('isCheque')?.value);
      console.log('- Bank Account ID:', this.expenseForm.get('bankAccountId')?.value);
      console.log('- Credit Card ID:', this.expenseForm.get('creditCardId')?.value);
    }, 100);
    
    console.log('Final form values after searchable dropdowns:', this.expenseForm.value);
    console.log('===================================');
  }

  // Fetch accounts from GraphQL API
  fetchAccounts() {
    this.chartOfAccountsService.fetchChartAccounts('').subscribe({
      next: (accounts) => {
        this.accounts = accounts;
        this.filteredAccounts.next(this.accounts.slice());
        this.initAccountSearch();
      },
      error: (error) => {
        console.error('Error fetching accounts:', error);
        this.toastr.error('Failed to fetch accounts', 'Error');
      }
    });
  }

  // Fetch employees from GraphQL API
  fetchEmployees() {
    this.employeeService.getEmployeeApprovers('').subscribe({
      next: (employees) => {
        this.employees = employees;
        this.filteredPaidByEmployees.next(this.employees.slice());
        this.initPaidBySearch();
      },
      error: (error) => {
        console.error('Error fetching employees:', error);
        this.toastr.error('Failed to fetch employees', 'Error');
      }
    });
  }

  // Fetch bank accounts from GraphQL API
  fetchBankAccounts() {
    this.bankService.fetchBankAccounts('').subscribe({
      next: (bankAccounts) => {
        this.bankAccounts = bankAccounts;
        this.filteredBankAccounts.next(this.bankAccounts.slice());
        this.initBankAccountSearch();
      },
      error: (error) => {
        console.error('Error fetching bank accounts:', error);
        this.toastr.error('Failed to fetch bank accounts', 'Error');
      }
    });
  }

  // Fetch credit cards from GraphQL API
  fetchCreditCards() {
    this.creditCardService.fetchCreditCards('').subscribe({
      next: (creditCards) => {
        this.creditCards = creditCards;
        this.filteredCreditCards.next(this.creditCards.slice());
        this.initCreditCardSearch();
      },
      error: (error) => {
        console.error('Error fetching credit cards:', error);
        this.toastr.error('Failed to fetch credit cards', 'Error');
      }
    });
  }

  // Initialize account search functionality
  initAccountSearch() {
    // Set initial value if editing
    if (this.expenseData?.accountId) {
      const selectedAccount = this.accounts.find(acc => acc.id === this.expenseData.accountId);
      if (selectedAccount) {
        this.accountFilterCtrl.setValue(selectedAccount.accountName);
      }
    }

    // Listen for search input changes
    this.accountFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterAccounts();
      });
  }

  // Filter accounts based on search input
  filterAccounts() {
    if (!this.accounts) {
      return;
    }
    
    // Get the search value
    let search = this.accountFilterCtrl.value;
    if (!search) {
      this.filteredAccounts.next(this.accounts.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    
    // Filter the accounts
    const filtered = this.accounts.filter(account => 
      account.accountName.toLowerCase().indexOf(search) > -1 ||
      account.accountCode.toLowerCase().indexOf(search) > -1
    );
    this.filteredAccounts.next(filtered);
  }

  // Handle account input
  onAccountInput() {
    this.filterAccounts();
  }

  // Handle account selection
  onAccountSelected(event: any) {
    const selectedAccount = event.option.value;
    this.expenseForm.patchValue({
      accountId: selectedAccount.id
    });
    // Update the display value to show account name
    this.accountFilterCtrl.setValue(`${selectedAccount.accountName} (${selectedAccount.accountCode})`);
  }

  // Initialize paid by search functionality
  initPaidBySearch() {
    // Set initial value if editing
    if (this.expenseData?.paidBy) {
      const selectedEmployee = this.employees.find(emp => emp.id === this.expenseData.paidBy);
      if (selectedEmployee) {
        this.paidByFilterCtrl.setValue(`${selectedEmployee.firstName} ${selectedEmployee.lastName} (${selectedEmployee.employeeCode})`);
      }
    }

    // Listen for search input changes
    this.paidByFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterPaidByEmployees();
      });
  }

  // Filter paid by employees
  filterPaidByEmployees() {
    if (!this.employees) {
      return;
    }
    
    let search = this.paidByFilterCtrl.value;
    if (!search) {
      this.filteredPaidByEmployees.next(this.employees.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    
    const filtered = this.employees.filter(employee => 
      `${employee.firstName} ${employee.lastName}`.toLowerCase().indexOf(search) > -1 ||
      employee.employeeCode.toLowerCase().indexOf(search) > -1
    );
    this.filteredPaidByEmployees.next(filtered);
  }

  // Filter bank accounts
  filterBankAccounts() {
    if (!this.bankAccounts) {
      return;
    }
    
    let search = this.bankAccountFilterCtrl.value;
    if (!search) {
      this.filteredBankAccounts.next(this.bankAccounts.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    
    const filtered = this.bankAccounts.filter(account =>
      account.bankName.toLowerCase().indexOf(search) > -1
    );
    this.filteredBankAccounts.next(filtered);
  }

  // Filter credit cards
  filterCreditCards() {
    if (!this.creditCards) {
      return;
    }
    
    let search = this.creditCardFilterCtrl.value;
    if (!search) {
      this.filteredCreditCards.next(this.creditCards.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    
    const filtered = this.creditCards.filter(card =>
      card.cardName.toLowerCase().indexOf(search) > -1 ||
      card.cardNumberMasked.toLowerCase().indexOf(search) > -1
    );
    this.filteredCreditCards.next(filtered);
  }

  // Handle paid by employee input
  onPaidByInput() {
    this.filterPaidByEmployees();
  }

  // Handle paid by employee selection
  onPaidBySelected(event: any) {
    const selectedEmployee = event.option.value;
    this.expenseForm.patchValue({
      paidBy: selectedEmployee.id
    });
    // Update the display value to show employee name
    this.paidByFilterCtrl.setValue(`${selectedEmployee.firstName} ${selectedEmployee.lastName}`);
  }

  // Handle bank account input
  onBankAccountInput() {
    this.filterBankAccounts();
  }

  // Handle bank account selection
  onBankAccountSelected(event: any) {
    const selectedBankAccount = event.option.value;
    this.expenseForm.patchValue({
      bankAccountId: selectedBankAccount.id
    });
    // Update the display value to show bank name
    this.bankAccountFilterCtrl.setValue(selectedBankAccount.bankName);
  }

  // Handle credit card input
  onCreditCardInput() {
    this.filterCreditCards();
  }

  // Handle credit card selection
  onCreditCardSelected(event: any) {
    const selectedCreditCard = event.option.value;
    this.expenseForm.patchValue({
      creditCardId: selectedCreditCard.id
    });
    // Update the display value to show card name and masked number
    this.creditCardFilterCtrl.setValue(`${selectedCreditCard.cardName} (${selectedCreditCard.cardNumberMasked})`);
  }

  // Handle payment method changes to reset conditional fields
  onPaymentMethodChange(paymentMethod: string) {
    console.log('Payment method changed to:', paymentMethod);
    
    // Reset bank-related fields when payment method is NOT 'BANK'
    if (paymentMethod !== 'BANK') {
      console.log('Resetting bank-related fields...');
      
      // Reset all bank-related form fields
      this.expenseForm.patchValue({ 
        isCheque: false,
        bankAccountId: null,
        chequeNo: '',
        chequeDate: null // Use null instead of empty string for date
      });
      
      // Clear the bank account filter control (autocomplete display)
      this.bankAccountFilterCtrl.setValue('');
      
      console.log('Bank-related fields reset successfully');
    }
    
    // Reset credit card fields when payment method is NOT 'CARD'
    if (paymentMethod !== 'CARD') {
      console.log('Resetting credit card fields...');
      
      this.expenseForm.patchValue({ creditCardId: null });
      this.creditCardFilterCtrl.setValue('');
      
      console.log('Credit card fields reset successfully');
    }
    
    console.log('Form after payment method change:', this.expenseForm.value);
    console.log('Bank account filter control value:', this.bankAccountFilterCtrl.value);
  }

  // Initialize searchable dropdown
  initSearchableDropdown() {
    // Set initial filtered expense types
    this.filteredExpenseTypes.next(this.expenseTypes.slice());

    // Set initial value if editing
    if (this.expenseData?.expenseType) {
      this.expenseTypeFilterCtrl.setValue(this.expenseData.expenseType);
    }

    // Listen for search input changes
    this.expenseTypeFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterExpenseTypes();
      });
  }

  // Filter expense types based on search input
  filterExpenseTypes() {
    if (!this.expenseTypes) {
      return;
    }
    
    // Get the search value
    let search = this.expenseTypeFilterCtrl.value;
    if (!search) {
      this.filteredExpenseTypes.next(this.expenseTypes.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    
    // Filter the expense types
    const filtered = this.expenseTypes.filter(expenseType => 
      expenseType.toLowerCase().indexOf(search) > -1
    );
    this.filteredExpenseTypes.next(filtered);
  }

  // Handle expense type input
  onExpenseTypeInput() {
    this.filterExpenseTypes();
  }

  // Handle expense type selection
  onExpenseTypeSelected(event: any) {
    this.expenseForm.patchValue({
      expenseType: event.option.value
    });
  }

  // Handle date change and format to DD/MM/YYYY
  onDateChange(event: any) {
    const selectedDate = event.value;
    if (selectedDate) {
      // Keep the Date object in the form control for the date picker
      // We'll format it to DD/MM/YYYY only for display purposes
      this.expenseForm.patchValue({
        expenseDate: selectedDate
      });
    }
  }

  // Handle cheque date change
  onChequeDateChange(event: any) {
    const selectedDate = event.value;
    if (selectedDate) {
      this.expenseForm.patchValue({
        chequeDate: selectedDate
      });
    }
  }

  // Format date for display in DD/MM/YYYY
  formatDateForDisplay(date: Date | string): string {
    if (!date) return '';
    if (typeof date === 'string') {
      return date;
    }
    return this.datePipe.transform(date, 'dd/MM/yyyy') || '';
  }

  initForm(): void {
    // Convert existing date string to Date object if it exists, or set to today for new expenses
    let initialDate: Date | string = new Date(); // Default to today for new expenses
    if (this.expenseData?.expenseDate) {
      console.log('Converting expense date:', this.expenseData.expenseDate, typeof this.expenseData.expenseDate);
      
      if (typeof this.expenseData.expenseDate === 'string') {
        // If it's already in DD/MM/YYYY format, convert to Date
        if (this.expenseData.expenseDate.includes('/')) {
          const dateParts = this.expenseData.expenseDate.split('/');
          if (dateParts.length === 3) {
            const day = parseInt(dateParts[0]);
            const month = parseInt(dateParts[1]) - 1; // Month is 0-indexed
            const year = parseInt(dateParts[2]);
            initialDate = new Date(year, month, day);
          }
        } else if (this.expenseData.expenseDate.includes('-')) {
          // If it's in YYYY-MM-DD format, convert to Date
          initialDate = new Date(this.expenseData.expenseDate);
        } else {
          // If it's a timestamp string, convert to Date
          const timestamp = parseInt(this.expenseData.expenseDate);
          if (!isNaN(timestamp)) {
            initialDate = new Date(timestamp);
          } else {
            initialDate = new Date(this.expenseData.expenseDate);
          }
        }
      } else if (typeof this.expenseData.expenseDate === 'number') {
        // If it's a timestamp number, convert to Date
        initialDate = new Date(this.expenseData.expenseDate);
      } else {
        initialDate = this.expenseData.expenseDate;
      }
      
      console.log('Converted initial date:', initialDate);
    }
    
    // Convert existing cheque date to Date object if it exists
    let initialChequeDate: Date | string = '';
    if (this.expenseData?.chequeDate) {
      console.log('Converting cheque date:', this.expenseData.chequeDate, typeof this.expenseData.chequeDate);
      if (typeof this.expenseData.chequeDate === 'string') {
        // If it's already in DD/MM/YYYY format, convert to Date
        if (this.expenseData.chequeDate.includes('/')) {
          const dateParts = this.expenseData.chequeDate.split('/');
          if (dateParts.length === 3) {
            const day = parseInt(dateParts[0]);
            const month = parseInt(dateParts[1]) - 1; // Month is 0-indexed
            const year = parseInt(dateParts[2]);
            initialChequeDate = new Date(year, month, day);
          }
        } 
        // If it's in YYYY-MM-DD format, convert to Date
        else if (this.expenseData.chequeDate.includes('-')) {
          initialChequeDate = new Date(this.expenseData.chequeDate);
        }
        // If it's a timestamp string (like "1756405800000"), convert to Date
        else {
          const timestamp = parseInt(this.expenseData.chequeDate);
          if (!isNaN(timestamp)) {
            initialChequeDate = new Date(timestamp);
          } else {
            initialChequeDate = new Date(this.expenseData.chequeDate);
          }
        }
      } else if (typeof this.expenseData.chequeDate === 'number') {
        // If it's a timestamp number, convert to Date
        initialChequeDate = new Date(this.expenseData.chequeDate);
      } else {
        initialChequeDate = this.expenseData.chequeDate;
      }
      console.log('Converted initial cheque date:', initialChequeDate);
    }

    this.expenseForm = this.fb.group({
      expenseDate: [initialDate, [Validators.required]],
      expenseType: [this.expenseData?.expenseType || '', [Validators.required]],
      accountId: [
        this.expenseData?.accountId || null,
        [Validators.required]
      ],
      amount: [
        this.expenseData?.amount || '',
        [Validators.required, Validators.min(0.01)]
      ],
      tax: [
        this.expenseData?.tax || 0,
        [Validators.min(0)]
      ],
      paidBy: [
        this.expenseData?.paidBy || null,
        [Validators.required]
      ],
      paymentMethod: [
        this.expenseData?.paymentMethod || '',
        [Validators.required]
      ],
      isCheque: [
        this.expenseData?.isCheque || false,
        [] // Optional
      ],
      bankAccountId: [
        this.expenseData?.bankAccountId || null,
        [] // Optional
      ],
      creditCardId: [
        this.expenseData?.creditCardId || null,
        [] // Optional
      ],
      chequeNo: [
        this.expenseData?.chequeNo || '',
        [] // Optional
      ],
      chequeDate: [
        initialChequeDate,
        [] // Optional
      ],
      referenceNo: [
        this.expenseData?.referenceNo || '',
        [] // Optional
      ],
      description: [
        this.expenseData?.description || '',
        [] // Optional
      ],
      file: [''] // File upload field
    });

    console.log('=== FORM INITIALIZATION DEBUG ===');
    console.log('Form values after init:', this.expenseForm.value);
    console.log('Payment method control value:', this.expenseForm.get('paymentMethod')?.value);
    console.log('isCheque control value:', this.expenseForm.get('isCheque')?.value);
    console.log('=================================');
  }

  onAddExpense(status: string = 'Pending'): void {
    console.log('onAddExpense called with status:', status);
    console.log('Form valid:', this.expenseForm.valid);
    console.log('Form value:', this.expenseForm.value);
    console.log('Loading state:', this.loading);
    
    // Check specific required fields
    console.log('Required field values:');
    console.log('- expenseDate:', this.expenseForm.get('expenseDate')?.value, 'valid:', this.expenseForm.get('expenseDate')?.valid);
    console.log('- expenseType:', this.expenseForm.get('expenseType')?.value, 'valid:', this.expenseForm.get('expenseType')?.valid);
    console.log('- accountId:', this.expenseForm.get('accountId')?.value, 'valid:', this.expenseForm.get('accountId')?.valid);
    console.log('- amount:', this.expenseForm.get('amount')?.value, 'valid:', this.expenseForm.get('amount')?.valid);
    console.log('- paidBy:', this.expenseForm.get('paidBy')?.value, 'valid:', this.expenseForm.get('paidBy')?.valid);
    console.log('- paymentMethod:', this.expenseForm.get('paymentMethod')?.value, 'valid:', this.expenseForm.get('paymentMethod')?.valid);
    
    if (this.expenseForm.valid) {
      this.loading = true;
      const formData = this.expenseForm.value;
      
      // Convert expense date to YYYY-MM-DD format
      if (formData.expenseDate instanceof Date) {
        const year = formData.expenseDate.getFullYear();
        const month = String(formData.expenseDate.getMonth() + 1).padStart(2, '0');
        const day = String(formData.expenseDate.getDate()).padStart(2, '0');
        formData.expenseDate = `${year}-${month}-${day}`;
      } else if (formData.expenseDate && formData.expenseDate.includes('/')) {
        const dateParts = formData.expenseDate.split('/');
        if (dateParts.length === 3) {
          const day = dateParts[0];
          const month = dateParts[1];
          const year = dateParts[2];
          formData.expenseDate = `${year}-${month}-${day}`;
        }
      }
      
      // Convert Cheque Date object to YYYY-MM-DD format for API if payment method is CHEQUE
      if (formData.paymentMethod === 'CHEQUE' && formData.chequeDate) {
        if (formData.chequeDate instanceof Date) {
          const year = formData.chequeDate.getFullYear();
          const month = String(formData.chequeDate.getMonth() + 1).padStart(2, '0');
          const day = String(formData.chequeDate.getDate()).padStart(2, '0');
          formData.chequeDate = `${year}-${month}-${day}`;
        } else if (formData.chequeDate && formData.chequeDate.includes('/')) {
          // Fallback for string format DD/MM/YYYY
          const dateParts = formData.chequeDate.split('/');
          if (dateParts.length === 3) {
            const day = dateParts[0];
            const month = dateParts[1];
            const year = dateParts[2];
            formData.chequeDate = `${year}-${month}-${day}`;
          }
        }
      }
      
      // Remove file field from formData and add attachmentUrl if files are uploaded
      const { file, ...formDataWithoutFile } = formData;
      
      // Remove empty fields and add conditional fields based on payment method
      const cleanedFormData: any = {};
      
      // Always include these fields
      cleanedFormData.expenseDate = formDataWithoutFile.expenseDate;
      cleanedFormData.expenseType = formDataWithoutFile.expenseType;
      cleanedFormData.accountId = formDataWithoutFile.accountId;
      cleanedFormData.amount = formDataWithoutFile.amount;
      cleanedFormData.tax = formDataWithoutFile.tax;
      cleanedFormData.paidBy = formDataWithoutFile.paidBy;
      cleanedFormData.paymentMethod = formDataWithoutFile.paymentMethod;
      cleanedFormData.referenceNo = formDataWithoutFile.referenceNo;
      cleanedFormData.description = formDataWithoutFile.description;
      
      // Add conditional fields based on payment method
      if (formDataWithoutFile.paymentMethod === 'BANK' && formDataWithoutFile.bankAccountId) {
        cleanedFormData.bankAccountId = formDataWithoutFile.bankAccountId;
      } else {
        cleanedFormData.bankAccountId = null;
      }
      
      if (formDataWithoutFile.paymentMethod === 'CARD' && formDataWithoutFile.creditCardId) {
        cleanedFormData.creditCardId = formDataWithoutFile.creditCardId;
      } else {
        cleanedFormData.creditCardId = null;
      }
      
      // Add isCheque field for BANK payment method
      if (formDataWithoutFile.paymentMethod === 'BANK') {
        cleanedFormData.isCheque = formDataWithoutFile.isCheque || false;
      } else {
        cleanedFormData.isCheque = false;
      }
      
      // Handle cheque fields based on isCheque checkbox
      if (formDataWithoutFile.paymentMethod === 'BANK' && formDataWithoutFile.isCheque) {
        if (formDataWithoutFile.chequeNo) {
          cleanedFormData.chequeNo = formDataWithoutFile.chequeNo;
        }
        if (formDataWithoutFile.chequeDate) {
          cleanedFormData.chequeDate = formDataWithoutFile.chequeDate;
        }
      } else {
        cleanedFormData.chequeNo = null;
        cleanedFormData.chequeDate = null;
      }
      
      // Add attachmentUrl if files are uploaded
      if (this.csvFile.length > 0) {
        const uploadedFile = this.csvFile[0] as any; // Cast to any to access url property
        if (uploadedFile.url) {
          cleanedFormData.attachmentUrl = uploadedFile.url;
        }
      }
      
      const expenseInput = cleanedFormData; // Use cleaned form data
      
      // Set the expense status based on the action
      expenseInput.expenseStatus = status;
      console.log('Setting expense status to:', status);
      
      if (this.expenseData?.id) {
        expenseInput.id = this.expenseData.id;
        // Update existing expense
        console.log('Sending update request with ID:', this.expenseData.id, 'and data:', expenseInput);
        this.expensesService.updateExpense(this.expenseData.id, expenseInput).subscribe({
          next: (response: any) => {
            console.log('Update response received:', response);
            this.loading = false;
            if (response) {
              console.log('Update successful, showing success message and closing dialog');
              this.toastr.success(`Expense ${status === 'Draft' ? 'saved as draft' : 'updated'} successfully!`, 'Success', {
                timeOut: 3000,
                progressBar: true,
                closeButton: true,
              });
              console.log('About to close dialog with result:', { success: true, data: response, action: 'update' });
              
              // Use setTimeout to ensure the dialog closes after the current execution cycle
              setTimeout(() => {
                console.log('Executing dialog close after timeout');
                this.dialogRef.close({ success: true, data: response, action: 'update' });
                this.csvFile = [];
              }, 100);
            } else if (response?.errors) {
              console.log('Update failed with errors:', response.errors);
              this.toastr.error(response?.errors[0]?.message, 'Error', {
                timeOut: 3000,
                progressBar: true,
                closeButton: true,
              });
            } else {
              console.log('Update response is empty or undefined, but no error occurred');
              // Even if response is empty, if we reach here without error, consider it successful
              this.toastr.success(`Expense ${status === 'Draft' ? 'saved as draft' : 'updated'} successfully!`, 'Success', {
                timeOut: 3000,
                progressBar: true,
                closeButton: true,
              });
              console.log('Closing dialog despite empty response');
              
              // Use setTimeout to ensure the dialog closes after the current execution cycle
              setTimeout(() => {
                console.log('Executing dialog close after timeout (empty response)');
                this.dialogRef.close({ success: true, data: null, action: 'update' });
                this.csvFile = [];
              }, 100);
            }
          },
          error: (error) => {
            console.log('Update error occurred:', error);
            this.loading = false;
            const errorMessage = error?.message || 'Failed to update expense';
            console.log('Showing error message:', errorMessage);
            this.toastr.error(errorMessage, 'Error', {
              timeOut: 3000,
              progressBar: true,
              closeButton: true,
            });
          },
        });
      } else {
        // Create new expense
        this.expensesService.createExpense(expenseInput).subscribe({
          next: (response: any) => {
            this.loading = false;
            if (response) {
              this.toastr.success(`Expense ${status === 'Draft' ? 'saved as draft' : 'added'} successfully!`, 'Success', {
                timeOut: 3000,
                progressBar: true,
                closeButton: true,
              });
              this.dialogRef.close({ success: true, data: response, action: 'create' });
              this.csvFile = [];
            } else if (response?.errors) {
              this.toastr.error(response?.errors[0]?.message, 'Error', {
                timeOut: 3000,
                progressBar: true,
                closeButton: true,
              });
            }
          },
          error: (error) => {
            this.loading = false;
            const errorMessage = error?.message || 'Failed to add expense';
            this.toastr.error(errorMessage, 'Error', {
              timeOut: 3000,
              progressBar: true,
              closeButton: true,
            });
          },
        });
      }
    } else {
      // Form is invalid - mark all fields as touched to show validation errors
      this.expenseForm.markAllAsTouched();
      
      // Show error message
      this.toastr.error('Please fill in all required fields correctly', 'Form Validation Error', {
        timeOut: 3000,
        progressBar: true,
        closeButton: true,
      });
      
      // Log form errors for debugging
      console.log('Form validation errors:', this.getFormValidationErrors());
    }
  }

  onSaveAsDraft(): void {
    console.log('Save as Draft clicked');
    this.onAddExpense('Draft');
  }

  // Helper method to get form validation errors for debugging
  private getFormValidationErrors() {
    const errors: any = {};
    Object.keys(this.expenseForm.controls).forEach(key => {
      const control = this.expenseForm.get(key);
      if (control && !control.valid && control.errors) {
        errors[key] = {
          value: control.value,
          errors: control.errors
        };
      }
    });
    return errors;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  // File upload methods
  removeFile(index: any) {
    this.csvFile.splice(index, 1);
    const input = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (input) {
      input.value = '';
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.csvFile.push(file);
    }
    this.uploadDocuments(this.csvFile);
  }

  uploadDocuments(csvFile: any) {
    console.log(csvFile);
    csvFile.forEach((file: any) => {
      this.documentUploadService.uploadDocument(file, 'expenses').subscribe({
        next: (url) => {
          this.url = url;
          file.url = url;
        },
        error: (error) => {
          console.log(error);
          this.toastr.error(error, 'File upload failed');
        },
      });
    });
  }

  onViewDocument(url: any) {
    const token = localStorage.getItem('AUTH_TOKEN');
    fetch(url, {
      method: 'GET',
      headers: {
        'authorization': token ? `Bearer ${token}` : '',
      }
    })
      .then(response => response.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      })
      .catch(error => console.error('Error:', error));
  }



  // Initialize employee search functionality
  initEmployeeSearch() {
    // Set initial values if editing
    if (this.expenseData?.paidBy) {
      const selectedEmployee = this.employees.find(emp => emp.id === this.expenseData.paidBy);
      if (selectedEmployee) {
        this.paidByFilterCtrl.setValue(`${selectedEmployee.firstName} ${selectedEmployee.lastName}`);
      }
    }

    // Listen for search input changes
    this.paidByFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterPaidByEmployees();
      });
  }

  // Initialize bank account search functionality
  initBankAccountSearch() {
    // Set initial value if editing
    if (this.expenseData?.bankAccountId) {
      const selectedAccount = this.bankAccounts.find(acc => acc.id === this.expenseData.bankAccountId);
      if (selectedAccount) {
        this.bankAccountFilterCtrl.setValue(selectedAccount.bankName);
      }
    }

    // Listen for search input changes
    this.bankAccountFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterBankAccounts();
      });
  }

  // Initialize credit card search functionality
  initCreditCardSearch() {
    // Set initial value if editing
    if (this.expenseData?.creditCardId) {
      const selectedCard = this.creditCards.find(card => card.id === this.expenseData.creditCardId);
      if (selectedCard) {
        this.creditCardFilterCtrl.setValue(`${selectedCard.cardName} (${selectedCard.cardNumberMasked})`);
      }
    }

    // Listen for search input changes
    this.creditCardFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterCreditCards();
      });
  }
}
